import { useEffect, useMemo, useRef, useState } from 'react';
import LoadingIcons from 'react-loading-icons';
import { useSelector } from 'react-redux';
import { SubscriptionTabs } from '..';
import { Subscription } from '../../../types/subscription';
import {
  useCalculateCurrentSingleProfitCalculationQuery,
  useFetchHeikinCandlesQuery,
} from '../../../store/services/api';
import { selectIsLoadingSubscription } from '../../../store/slices/subscription';
import useSubscriptionDetailSource from '../../../hooks/useSubscriptionDetailSource';
import { profitWashColor } from '../../../colors';
import { TimeFrame } from '../../../types/Bot';
import { getTimeFrameIntervalTimestamp } from '../../Detail/Charts/PriceChart/helpers';
import NoSubscriptionsNotice from '../../../components/NoSubscriptionsNotice';
import GlobalLoader from '../../../components/GlobalLoader';
import Calculations from './Calculations';
import Header from '../Header';
import Overview from '../Overview';
import Transactions from '../Transactions';
import Manage from '../Manage';

interface Props {
  activeTab: SubscriptionTabs;
  subscription: Subscription | null;
}

/**
 * The Result page (SpcResultView) mounts many Victory charts in one synchronous
 * pass that briefly blocks the main thread. Render a spinner first and defer the
 * heavy mount to a later frame (double rAF) so the spinner actually paints — the
 * click stays instant and a loading state shows during the render instead of a
 * frozen screen.
 */
const DeferredCalculations = ({
  subscription,
}: {
  subscription: Subscription;
}) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setReady(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 400,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <LoadingIcons.Grid width={60} />
      </div>
    );
  }
  return <Calculations subscription={subscription} />;
};

const Content = ({ activeTab, subscription }: Props) => {
  const isSubscriptionLoading = useSelector(selectIsLoadingSubscription);
  // Bot + detail with demo/real picking and window clipping concentrated in
  // one hook so this screen has no demo awareness.
  const { bot, detail } = useSubscriptionDetailSource(subscription);

  // Full SPC result — reliable source of strategy config (indicator + MRC).
  // calculateCurrentSingleProfitCalculation is already userless + cached SPC,
  // so it works identically for real and demo users.
  const { data: spcResult } = useCalculateCurrentSingleProfitCalculationQuery(
    subscription?.baseAsset ?? '',
    { skip: !subscription?.baseAsset },
  );
  const spc = spcResult?.data;

  const onTransactions = activeTab === SubscriptionTabs.Transactions;

  const firstTransactionTimestamp = useMemo(() => {
    const timestamps = (subscription?.transactions ?? [])
      .map((tx) => tx.transactionTimestamp)
      .filter((value): value is number => typeof value === 'number')
      .sort((a, b) => a - b);
    return timestamps[0];
  }, [subscription?.transactions]);

  // Shorter-timeframe candles for the live/expanded charts (Transactions tab).
  const shorterTimeFrame = spc?.shorterTimeFrame ?? bot?.timeFrame?.shorter;
  const candleParams = useMemo(() => {
    if (
      !subscription ||
      !shorterTimeFrame ||
      !firstTransactionTimestamp ||
      !bot?.defaultExchange
    ) {
      return undefined;
    }
    return {
      baseAsset: subscription.baseAsset,
      quoteAsset: subscription.quoteAsset,
      timeFrame: shorterTimeFrame,
      startTimestamp:
        firstTransactionTimestamp -
        getTimeFrameIntervalTimestamp(shorterTimeFrame as TimeFrame) * 3,
      endTimestamp: new Date().valueOf(),
      includeLatestCandle: true,
      exchange: bot.defaultExchange,
    };
  }, [
    subscription,
    shorterTimeFrame,
    firstTransactionTimestamp,
    bot?.defaultExchange,
  ]);

  const { data: shorterTCandles } = useFetchHeikinCandlesQuery(candleParams, {
    skip: !candleParams || !onTransactions,
  });

  // Reset scroll to the top whenever the active section changes.
  const topRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [activeTab]);

  if (!subscription) {
    return <NoSubscriptionsNotice />;
  }

  const needsDetail =
    activeTab === SubscriptionTabs.Overview ||
    activeTab === SubscriptionTabs.Transactions;
  const isLoadingDetail =
    needsDetail &&
    isSubscriptionLoading &&
    typeof subscription.chartData === 'undefined';

  // The Calculations tab renders the shared Result page, which adds its own 32px
  // horizontal padding. Reduce the wrapper padding there so its content lands at
  // the same 40px inset as the other sections.
  const horizontalPadding =
    activeTab === SubscriptionTabs.Calculations ? 8 : 40;

  const maxDecimals = spc?.maxDecimals ?? bot?.complexParams?.maxDecimals ?? 6;

  // Full-bleed profit wash behind the top of the content, mirroring the Overview
  // hero gradient but ~double the height.
  //
  // IMPORTANT: this only shows because the surfaces stacked on top of it are
  // transparent. If the wash disappears, check that these still have
  // `background: transparent` (they default to the page's #000 elsewhere):
  //   Header, Overview wrapper, Transactions wrapper, Manage wrapper,
  //   and the shared Result `ProfitChart` + `LiveState`.
  const gradientColor = profitWashColor((subscription.currentProfit || 0) >= 0);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingLeft: horizontalPadding,
        paddingRight: horizontalPadding,
        background: `linear-gradient(180deg, ${gradientColor} 0%, rgba(0,0,0,0) 460px)`,
      }}
    >
      <div ref={topRef} />

      {/* Calculations brings its own masthead (SpcResultView) */}
      {activeTab !== SubscriptionTabs.Calculations && (
        <Header subscription={subscription} maxDecimals={maxDecimals} />
      )}

      {isLoadingDetail ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingIcons.Grid width={60} />
        </div>
      ) : (
        <>
          {activeTab === SubscriptionTabs.Overview && (
            <Overview
              subscription={subscription}
              bot={bot}
              spc={spc}
              chartData={detail?.chartData}
            />
          )}
          {activeTab === SubscriptionTabs.Transactions && (
            <Transactions
              subscription={subscription}
              bot={bot}
              spc={spc}
              chartData={detail?.chartData}
              shorterCandles={shorterTCandles?.normal}
            />
          )}
          {activeTab === SubscriptionTabs.Calculations && (
            <DeferredCalculations subscription={subscription} />
          )}
          {activeTab === SubscriptionTabs.Manage && (
            <Manage subscription={subscription} bot={bot} />
          )}
        </>
      )}

      {isSubscriptionLoading && !!subscription.chartData && <GlobalLoader />}
    </div>
  );
};

export default Content;
